import { useEffect } from 'react';
import type {
	CiselnikyCiselnikPolozkaDto,
	OsobyFyzickaOsobaDto,
} from '@gov-nx/api';
import {
	useContactDetailsDefinition,
	useCountryCodes,
	usePersonCommunicationForm,
} from '@gov-nx/core/hooks';
import type { FormDefinition2 } from '@gov-nx/core/service';
import { useFormBuilderWithConsent, useLocale } from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import {
	getSelectDefinition,
	phoneShapeToString,
	toDateReadable,
	useAddressSearchDefinition,
	useInputDefinition,
} from '@gov-nx/utils/common';
import type { PageCode } from '../../definitions/codes';
import type {
	DataRequest,
	FormData,
	FormFields,
	ProfileRobComplaintProcessControl,
} from './context.types';
import { useSelectOptions } from './options';
import { getSortedOptions } from './utils';

export interface FormInstanceProps {
	code: PageCode;
	data: Nullable<OsobyFyzickaOsobaDto>;
	email?: string;
	phone?: string;
	countries: CiselnikyCiselnikPolozkaDto[];
	controls: ProfileRobComplaintProcessControl;
	isLowAuthorization: boolean;
}

export interface FormInstanceType {
	form: FormDefinition2<FormData, FormFields>;
	hasCountriesOptions: boolean;
}

export function FormInstance({
	code,
	data,
	email,
	phone,
	countries,
	controls,
	isLowAuthorization,
}: FormInstanceProps): FormInstanceType {
	const { t } = useLocale(code);
	const isDisabled = controls.processLoading;
	const countriesOptions = getSortedOptions(countries);

	const {
		POHLAVI_OPTIONS,
		OMEZENI_SVEPRAVNOSTI_OPTIONS,
		RODINNY_STAV_PARTNERSTVI_OPTIONS,
	} = useSelectOptions();

	const jmeno = useInputDefinition({
		name: 'jmeno',
		label: t('formular.pole.jmeno'),
		isDisabled,
		placeholder: t('formular.placeholders.jmeno'),
	});

	const prijmeni = useInputDefinition({
		name: 'prijmeni',
		label: t('formular.pole.prijmeni'),
		isDisabled,
		placeholder: t('formular.placeholders.prijmeni'),
	});

	const rodnePrijmeni = useInputDefinition({
		name: 'rodnePrijmeni',
		label: t('formular.pole.rodne-prijmeni'),
		isDisabled,
		placeholder: t('formular.placeholders.rodne-prijmeni'),
	});

	const rodinnyStavPartnerstvi = getSelectDefinition({
		name: 'rodinnyStavPartnerstvi',
		label: t('formular.pole.rodinny-stav-partnerstvi'),
		isDisabled,
		options: RODINNY_STAV_PARTNERSTVI_OPTIONS,
	});

	const pohlavi = getSelectDefinition({
		name: 'pohlavi',
		label: t('formular.pole.pohlavi'),
		isDisabled,
		options: POHLAVI_OPTIONS,
	});

	const datumNarozeni = useInputDefinition({
		name: 'datumNarozeni',
		label: t('formular.pole.datum-narozeni'),
		isDisabled: true,
		placeholder: t('formular.placeholders.datum-narozeni'),
		defaultValue:
			data && data.datumNarozeni ? toDateReadable(data.datumNarozeni) : '',
	});

	const stat = getSelectDefinition({
		name: 'stat',
		label: t('formular.pole.stat'),
		isRequiredWhen: {
			fieldName: 'newPlaceOfBirth',
			is: true,
			errorMessage: t('formular.disclaimer.stat'),
		},
		isDisabled,
		options: [
			{ label: t('formular.pole.neuvedeno'), value: '', isDisabled: true },
			...countriesOptions,
		],
	});

	const newPlaceOfBirth = useInputDefinition({
		name: 'newPlaceOfBirth',
		label: t('formular.pole.misto'),
		isDisabled,
		placeholder: t('formular.placeholders.misto'),
	});

	const addressSearchDefinition = useAddressSearchDefinition({
		isDisabled,
		isSearchShown: controls.displaySearchAddress,
		strings: {
			nazevObce: {
				label: t('formular.pole.nazev-obce'),
				placeholder: t('formular.placeholders.nazev-obce'),
				requiredMessage: t('formular.validace.nazevObce.povinny'),
			},
			nazevUlice: {
				label: t('formular.pole.nazev-ulice'),
				placeholder: t('formular.placeholders.nazev-ulice'),
			},
			cislo: {
				label: t('formular.pole.cislo-evidencni-popisne'),
				placeholder: t('formular.placeholders.cislo-evidencni-popisne'),
				requiredMessage: t('formular.validace.cislo.povinny'),
				max9999Message: t('formular.validace.max-9999'),
				onlyNumbersMessage: t('formular.validace.pouze-cislice'),
			},
		},
	});

	const newAddress = getSelectDefinition({
		name: 'newAddress',
		label: t('formular.pole.adresa-pobytu'),
		isDisabled,
		options: [],
		// note: adress options are handled in AddressSearchForm component
	});

	const nazevObceDorucovaciAdresa = useInputDefinition({
		name: 'nazevObceDorucovaciAdresa',
		label: t('formular.pole.nazev-obce'),
		isDisabled,
		placeholder: t('formular.placeholders.nazev-obce'),
		isRequired: controls.displaySearchDeliveryAddress,
	});

	const nazevUliceDorucovaciAdresa = useInputDefinition({
		name: 'nazevUliceDorucovaciAdresa',
		label: t('formular.pole.nazev-ulice'),
		isDisabled,
		placeholder: t('formular.placeholders.nazev-ulice'),
	});

	const cisloDorucovaciAdresa = useInputDefinition({
		name: 'cisloDorucovaciAdresa',
		label: t('formular.pole.cislo-evidencni-popisne'),
		isDisabled,
		placeholder: t('formular.placeholders.cislo-evidencni-popisne'),
		max: { max: 4, errorMessage: t('formular.validace.max-9999') },
		onlyNumbers: { errorMessage: t('formular.validace.pouze-cislice') },
		isRequired: controls.displaySearchDeliveryAddress,
	});

	const newDeliveryAddress = getSelectDefinition({
		name: 'newDeliveryAddress',
		label: t('formular.pole.dorucovaci-adresa'),
		isDisabled,
		options: [],
		// node: adress options are handled in DeliveryAddressSearchForm component
	});

	const omezeniSvepravnosti = getSelectDefinition({
		name: 'omezeniSvepravnosti',
		label: t('formular.pole.omezeni-svepravnosti'),
		isDisabled,
		options: OMEZENI_SVEPRAVNOSTI_OPTIONS,
	});

	const popisReklamace = useInputDefinition({
		name: 'popisReklamace',
		label: t('formular.pole.popis-reklamace'),
		isDisabled,
		rows: 5,
		placeholder: t('formular.placeholders.popis-reklamace'),
		isRequired: true,
		isRequiredMessage: t('formular.validace.popisReklamace.povinny'),
	});

	const contactDetails = useContactDetailsDefinition({
		isDisabled,
		strings: {
			_useEmail: { label: t('formular.pole.poskytuji-email') },
			_usePhone: { label: t('formular.pole.poskytuji-telefon') },
			email: {
				label: '',
				placeholder: t('kontaktni-udaje.email.placeholder'),
			},
			phone: {
				label: '',
				placeholder: t('kontaktni-udaje.phone.placeholder'),
			},
		},
	});

	const form = useFormBuilderWithConsent<FormData, FormFields>(
		[
			jmeno,
			prijmeni,
			rodnePrijmeni,
			rodinnyStavPartnerstvi,
			pohlavi,
			datumNarozeni,
			stat,
			newPlaceOfBirth,
			...addressSearchDefinition,
			newAddress,
			nazevObceDorucovaciAdresa,
			nazevUliceDorucovaciAdresa,
			cisloDorucovaciAdresa,
			newDeliveryAddress,
			omezeniSvepravnosti,
			popisReklamace,
			...contactDetails,
		],
		{ isLowAuthorization, isDisabled: isDisabled }
	);

	usePersonCommunicationForm(form, {
		countryCodes: useCountryCodes(),
		email,
		phone,
	});

	const poskytnoutEmail = form.formMethods.watch('_useEmail');
	const poskytnoutTelefon = form.formMethods.watch('_usePhone');

	useEffect(() => {
		if (!poskytnoutEmail) {
			form.formMethods.clearErrors('email');
		}
		if (!poskytnoutTelefon) {
			form.formMethods.clearErrors('phone');
		}
	}, [form.formMethods, poskytnoutEmail, poskytnoutTelefon]);

	return {
		form,
		hasCountriesOptions: countriesOptions.length > 0,
	};
}

export const prepareSubmitData =
	(
		newAddress?: string,
		newDeliveryAddress?: string,
		newPlaceOfBirth?: string
	) =>
	(data: FormData): DataRequest => {
		const seznam = [];
		if (data.jmeno) {
			seznam.push({
				stavUdaje: 'NESPRAVNY',
				kodUdaje: 'Jmeno',
				zpusobNaplneni: data.jmeno,
			});
		}
		if (data.prijmeni) {
			seznam.push({
				stavUdaje: 'NESPRAVNY',
				kodUdaje: 'Prijmeni',
				zpusobNaplneni: data.prijmeni,
			});
		}
		if (data.rodnePrijmeni) {
			seznam.push({
				stavUdaje: 'NESPRAVNY',
				kodUdaje: 'RodnePrijmeni',
				zpusobNaplneni: data.rodnePrijmeni,
			});
		}
		if (data.rodinnyStavPartnerstvi) {
			seznam.push({
				stavUdaje: 'NESPRAVNY',
				kodUdaje: 'RodinnyStavPartnerstvi',
				zpusobNaplneni: data.rodinnyStavPartnerstvi,
			});
		}
		if (data.pohlavi) {
			seznam.push({
				stavUdaje: 'NESPRAVNY',
				kodUdaje: 'Pohlavi',
				zpusobNaplneni: data.pohlavi,
			});
		}
		if (data.stat) {
			seznam.push({
				stavUdaje: 'NESPRAVNY',
				kodUdaje: 'MistoNarozeniSvetStat',
				zpusobNaplneni: data.stat,
			});
		}
		if (newPlaceOfBirth) {
			seznam.push({
				stavUdaje: 'NESPRAVNY',
				kodUdaje: 'MistoNarozeniSvetMisto',
				zpusobNaplneni: newPlaceOfBirth,
			});
		}
		if (newAddress) {
			seznam.push({
				stavUdaje: 'NESPRAVNY',
				kodUdaje: 'AdresaPobytu',
				zpusobNaplneni: newAddress,
			});
		}
		if (newDeliveryAddress) {
			seznam.push({
				stavUdaje: 'NESPRAVNY',
				kodUdaje: 'DorucovaciAdresa',
				zpusobNaplneni: newDeliveryAddress,
			});
		}
		if (data.omezeniSvepravnosti) {
			seznam.push({
				stavUdaje: 'NESPRAVNY',
				kodUdaje: 'OmezeniSvepravnosti',
				zpusobNaplneni: data.omezeniSvepravnosti,
			});
		}
		return {
			params: {},
			body: {
				typReklamace: 'REKLAMACE_EXISTUJICIHO_SUBJEKTU',
				navrhovaneHodnoty: { seznam },
				popisReklamace: data.popisReklamace,
				kontaktniEmail: data._useEmail ? data.email : undefined,
				kontaktniTelefon:
					data._usePhone && data.phone
						? phoneShapeToString(data.phone)
						: undefined,
			},
		};
	};
